import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Check } from "lucide-react-native";
import React from "react";
import type { SectionStatus } from "./sectionStatus";
import useJustFlipped from "./useJustFlipped";

type Props = {
  status: SectionStatus;
};

const LABEL: Record<SectionStatus, string> = {
  ready: "Listo",
  writing: "Trabajando…",
  pending: "Pendiente",
};

/**
 * The section's status, next to its header.
 *
 * "Trabajando…" pulses (the agent is genuinely mid-write, so a static chip would
 * lie about it being idle) and "Listo" gets a one-shot flip acknowledgment the
 * moment it flips true — the same `flash-success` / `step-flip` mechanics the old
 * draft panel used, because a preview that silently refetches while the agent
 * narrates the same thing on the left is easy to miss otherwise.
 */
const StatusChip = React.memo(({ status }: Props) => {
  const justFlipped = useJustFlipped(status === "ready");

  return (
    <Box className="relative">
      {justFlipped ? (
        <Box className="absolute -inset-x-1.5 -inset-y-1 rounded-full bg-success-50 animate-flash-success" />
      ) : null}
      {/* The `key` forces a remount exactly when the status flips to "ready": a CSS
          animation class doesn't replay just because a prop changed on the same
          element, only on mount. */}
      <Badge
        key={status}
        action={
          status === "ready"
            ? "success"
            : status === "writing"
              ? "brand"
              : "muted"
        }
        variant={status === "pending" ? "outline" : "solid"}
        size="sm"
        rounded="full"
        className={
          status === "ready"
            ? "animate-step-flip"
            : status === "writing"
              ? "animate-shimmer motion-reduce:animate-none"
              : undefined
        }
      >
        {status === "ready" ? <BadgeIcon as={Check} /> : null}
        <BadgeText>{LABEL[status]}</BadgeText>
      </Badge>
    </Box>
  );
});

StatusChip.displayName = "StatusChip";

export default StatusChip;
